/* eslint-disable complexity */
import type { ComponentProps } from 'react';
import React, { useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ContentFullContentImageSet, ContentImage, ContentLink } from 'graphql-ts-types';
import type {
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  MarginBottomProps,
  MarginLeftProps,
  MarginRightProps,
  MaxWidthProps,
  MinWidthProps,
  PaddingRightProps,
  PositionProps,
  RightProps,
  SizeProps,
  SpaceProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  marginBottom,
  marginLeft,
  marginRight,
  maxWidth,
  minWidth,
  paddingRight,
  position,
  right,
  size,
  space,
  top,
  width,
  zIndex,
} from 'styled-system';

import { parseExtraDisclaimerContentToText } from 'common/components/DiscountLabels/LabelDrawerContent';
import { Flex } from 'common/components/Flex';
import PrismicResponsiveImage from 'common/components/PrismicResponsiveImage';
import Search from 'common/components/Search/Search';
import { LinkRegularLR2 } from 'common/components/Typography';
import { useMatchesViewport } from 'common/hooks/media';
import { useAutoLogoutBannerHeight } from 'common/hooks/useAutoLogoutBannerHeight';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import type { SearchParams } from 'common/types/search';
import { transformToPrismicImage } from 'common/utils/image';
import { SideNavType } from 'common/utils/navigation';
import SideNav from '../../side-nav/components/SideNav';
import {
  CONTAINER_MAX_WIDTH,
  DEFAULT_STICKY_TOP_PX,
  NAV_SEARCH_Z_INDEX,
  NAV_Z_INDEX,
  SCROLL_DIRECTIONS,
  SEARCH_WIDTHS,
} from './constants';
import type { ScrollDirectionType } from './hooks/useOnScrollChange';
import useOnScrollChange from './hooks/useOnScrollChange';
import LegalDisclaimer from './LegalDisclaimer';
import MenuWithLogo from './MenuWithLogo';
import { Container, containerStyles, getMinimalSearchStyles, Grid, gridStyles } from './styles';

const SearchContainer = styled.div<
  {
    scrollDirection: Nullable<ScrollDirectionType>;
    showAnimation: boolean;
  } & PaddingRightProps &
    MinWidthProps &
    SpaceProps &
    PositionProps &
    ZIndexProps &
    TopProps &
    RightProps &
    BorderRadiusProps &
    BorderColorProps &
    BorderWidthProps &
    BorderStyleProps &
    SizeProps
>`
  ${paddingRight};
  ${minWidth};
  ${space};
  ${position};
  ${zIndex};
  ${top};
  ${right};
  ${borderRadius};
  ${borderColor};
  ${borderWidth};
  ${borderStyle};
  ${size};

  ${props =>
    props.showAnimation &&
    props.scrollDirection === SCROLL_DIRECTIONS.DOWN &&
    css`
      @keyframes shrinkContainerWidth {
        from {
          min-width: 100%;
        }
        to {
          min-width: fit-content;
        }
      }
      animation: shrinkContainerWidth 0.8s ease-in-out forwards;
    `}

  ${props =>
    props.showAnimation &&
    props.scrollDirection === SCROLL_DIRECTIONS.UP &&
    css`
      @keyframes expandContainerWidth {
        from {
          min-width: 24px;
          width: 24px;
        }
        to {
          min-width: 100%;
        }
      }
      animation: expandContainerWidth 0.3s ease-in-out forwards;
    `}
`;

type SearchContainerStyles = Omit<
  ComponentProps<typeof SearchContainer>,
  'showAnimation' | 'scrollDirection' | 'isMobile'
>;

const getSearchContainerStyles = (
  withAnimation: boolean,
  isMobile: boolean
): SearchContainerStyles => ({
  minWidth: SEARCH_WIDTHS,
  ...getMinimalSearchStyles(withAnimation, isMobile),
});

const Banner = styled(PrismicResponsiveImage)<
  WidthProps & MarginBottomProps & MarginLeftProps & MarginRightProps & MaxWidthProps & DisplayProps
>`
  ${display};
  ${width};
  ${marginBottom};
  ${marginLeft};
  ${marginRight};
  ${maxWidth};
`;

const bannerStyles: Partial<ComponentProps<typeof Banner>> = {
  mb: -4,
  width: '100%',
};

const BannerWrapper = styled.div<PositionProps & WidthProps>`
  ${position};
  ${width};
`;

export type NavigationOptions = {
  isMobileStickyExpanded: boolean;
  isMobileStickyMinimal: boolean;
  isNonMobileSticky: boolean;
  isMobile: boolean;
  height: number;
};

export interface ShopInShopNavigationProps {
  navigation: GraphqlNavigationItemProps;
  searchParams: SearchParams;
  filterQuery?: string;
  shopName: string;
  linkLabel?: string;
  drawerContent?: string;
  homepage: Nullable<Pick<ContentLink, 'fullUrl'>>;
  banner?: Nullable<ContentFullContentImageSet>;
  logo: Pick<ContentImage, 'url' | 'alt'>;
  navContainerStyles?: ComponentProps<typeof Container>;
  // Controls the mobile view animation. When disabled, mobile view will have the minimal header with only search icon
  withAnimation?: boolean;
}

const ShopInShopNavigation = ({
  searchParams,
  navigation,
  filterQuery,
  shopName,
  homepage,
  banner,
  logo,
  withAnimation = true,
  linkLabel,
  drawerContent,
}: ShopInShopNavigationProps) => {
  const shopUrl = homepage?.fullUrl ?? undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollDirection, onDirectionChange] = useState<Nullable<ScrollDirectionType>>(null);
  const {
    matches: { matchesTablet },
  } = useMatchesViewport();

  const isMobile = !matchesTablet;
  const isMinimalHeader =
    isMobile && (!withAnimation || (withAnimation && scrollDirection === SCROLL_DIRECTIONS.DOWN));

  const autoLogoutBannerHeight = useAutoLogoutBannerHeight();
  // Sticky top offset: Always uses -1px (DEFAULT_STICKY_TOP_PX, from default style) to hide the top border for cleaner visuals.
  // When auto logout banner is present, positions nav below it while maintaining the -1px offset.
  // Examples: banner height 50px → 49px offset | no banner → -1px offset
  const stickyTopOffset =
    autoLogoutBannerHeight > 0
      ? autoLogoutBannerHeight + DEFAULT_STICKY_TOP_PX
      : DEFAULT_STICKY_TOP_PX;

  useOnScrollChange({ onDirectionChange, containerRef });

  return (
    <>
      {banner && (
        <Flex
          justifyContent="center"
          marginLeft="auto"
          marginRight="auto"
          maxWidth={CONTAINER_MAX_WIDTH}
        >
          <BannerWrapper position="relative" width="100%">
            <Banner {...bannerStyles} image={transformToPrismicImage(banner)} />
            {linkLabel && drawerContent && (
              <LegalDisclaimer
                color="sys.neutral.text.strong"
                content={parseExtraDisclaimerContentToText(drawerContent)}
                label={<LinkRegularLR2>{linkLabel}</LinkRegularLR2>}
              />
            )}
          </BannerWrapper>
        </Flex>
      )}
      <Container
        data-corgi-id="shop-in-shop-navigation"
        ref={containerRef}
        {...containerStyles}
        top={stickyTopOffset}
      >
        <SideNav
          navigation={navigation}
          logoUrl={logo.url}
          shopName={shopName}
          shopUrl={shopUrl}
          withBottomSection={false}
          type={SideNavType.ShopInShop}
        />
        <Grid {...gridStyles}>
          <MenuWithLogo
            shopName={shopName}
            logo={logo}
            navigation={navigation}
            isMinimalHeader={isMinimalHeader}
            shopUrl={shopUrl}
          />
          {filterQuery && (
            <SearchContainer
              showAnimation={isMobile && withAnimation}
              scrollDirection={scrollDirection}
              position="relative"
              zIndex={NAV_SEARCH_Z_INDEX}
              {...getSearchContainerStyles(withAnimation, isMobile)}
            >
              <Search
                params={searchParams}
                withRecentSearches={false}
                withProductSuggestions={false}
                iconVariant="secondary"
                filterQuery={filterQuery}
                shopName={shopName}
                showOnlySearchIcon={isMinimalHeader}
                withAnimation={withAnimation}
                minimalIcon={isMinimalHeader}
                overlayStacking={NAV_Z_INDEX}
                parentRef={containerRef}
              />
            </SearchContainer>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default ShopInShopNavigation;
